import {
  type Accessor,
  type JSX,
  createEffect,
  createSignal,
  on,
  onCleanup,
} from "solid-js";

type Props = JSX.Directives["floating"] & object;

export type FloatingElement = {
  config: () => Props;
  element: HTMLElement;
  hide: () => void;
  show: Accessor<Props | undefined>;
};

const [floatingElements, setFloatingElements] = createSignal<FloatingElement[]>(
  [],
);

export { floatingElements };

/**
 * Register a new floating element
 * @param element element
 */
export function registerFloatingElement(element: FloatingElement) {
  setFloatingElements((elements) => [...elements, element]);
}

/**
 * Un register floating element
 * @param element DOM Element
 */
export function unregisterFloatingElement(element: HTMLElement) {
  setFloatingElements((elements) =>
    elements.filter((entry) => entry.element !== element),
  );
}

/**
 * Add floating elements
 * @param element Element
 * @param accessor Parameters
 */
export function floating(element: HTMLElement, accessor: Accessor<Props>) {
  const config = accessor();
  if (!config) return;

  const [show, setShow] = createSignal<Props | undefined>();
  // DEBUG: createEffect(() => console.info("show:", show()));

  registerFloatingElement({
    config: accessor,
    element,
    show,
    /**
     * Hide the element
     */
    hide() {
      setShow(undefined);
    },
  });

  /**
   * Trigger a floating element
   */
  function trigger(target: keyof Props, desiredState?: boolean) {
    const current = show();
    const config = accessor();

    if (target === "userCard" && config.userCard) {
      if (current?.userCard) {
        setShow(undefined);
      } else if (!current) {
        setShow({ userCard: config.userCard });
      } else {
        setShow(undefined);
        setShow({ userCard: config.userCard });
      }
    }

    if (target === "tooltip" && config.tooltip) {
      if (current?.tooltip) {
        if (desiredState !== true) {
          setShow(undefined);
        }
      } else if (!current) {
        if (desiredState !== false) {
          setShow({ tooltip: config.tooltip });
        }
      }
    }

    if (target === "contextMenu" && config.contextMenu) {
      if (current?.contextMenu) {
        setShow(undefined);
      } else if (!current) {
        setShow({ contextMenu: config.contextMenu });
      } else {
        setShow(undefined);
        setShow({ contextMenu: config.contextMenu });
      }
    }
  }

  /**
   * Handle click events
   */
  function onClick() {
    // TODO: handle shift+click for mention
    trigger("userCard");
  }

  /**
   * Handle context menu click
   */
  function onContextMenu(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    trigger("contextMenu");
  }

  let isTouching = false,
    tTmr: NodeJS.Timeout | undefined;

  /**
   * Handle mouse entering
   */
  function onMouseEnter() {
    if (!isTouching) trigger("tooltip", true);
  }

  /**
   * Handle mouse leaving
   */
  function onMouseLeave() {
    trigger("tooltip", false);
  }

  function onTouch() {
    isTouching = true;
    clearTimeout(tTmr);
    tTmr = setTimeout(() => {
      isTouching = false;
      tTmr = undefined;
    }, 100);
  }

  createEffect(
    on(
      () => accessor().userCard,
      (userCard) => {
        if (userCard) {
          element.style.cursor = "pointer";
          element.style.userSelect = "none";
          element.addEventListener("click", onClick);

          onCleanup(() => element.removeEventListener("click", onClick));
        }
      },
    ),
  );

  createEffect(
    on(
      () => accessor().tooltip,
      (tooltip) => {
        if (tooltip) {
          element.ariaLabel =
            typeof tooltip.content === "string"
              ? tooltip.content
              : tooltip!.aria!;

          element.addEventListener("mouseenter", onMouseEnter);
          element.addEventListener("mouseleave", onMouseLeave);
          element.addEventListener("touchstart", onTouch);
          element.addEventListener("touchend", onTouch);

          onCleanup(() => {
            element.removeEventListener("mouseenter", onMouseEnter);
            element.removeEventListener("mouseleave", onMouseLeave);
            element.addEventListener("touchstart", onTouch);
            element.addEventListener("touchend", onTouch);
          });
        }
      },
    ),
  );

  createEffect(
    on(
      () => accessor().contextMenu,
      (contextMenu) => {
        if (contextMenu) {
          const handler = accessor().contextMenuHandler ?? "contextmenu";
          element.addEventListener(handler, onContextMenu);

          // Long-press for touchscreens: iOS never fires `contextmenu`, it
          // starts text selection instead — detect the press ourselves.
          let lpTimer: NodeJS.Timeout | undefined;
          let lpFired = false;
          let lpX = 0,
            lpY = 0;

          function lpCancel() {
            element.style.webkitUserSelect = "";
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (element.style as any).webkitTouchCallout = "";
            if (lpTimer) {
              clearTimeout(lpTimer);
              lpTimer = undefined;
            }
          }

          function lpStart(e: TouchEvent) {
            if (e.touches.length !== 1) return lpCancel();
            lpFired = false;
            lpX = e.touches[0].clientX;
            lpY = e.touches[0].clientY;

            // suppress text selection / iOS callout while pressing
            element.style.webkitUserSelect = "none";
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (element.style as any).webkitTouchCallout = "none";

            lpTimer = setTimeout(() => {
              lpTimer = undefined;
              lpFired = true;
              navigator.vibrate?.(30);
              // open on a fresh frame so this press isn't seen as the
              // outside-tap that would immediately dismiss the menu
              setTimeout(() => trigger("contextMenu"), 0);
            }, 450);
          }

          function lpMove(e: TouchEvent) {
            const t = e.touches[0];
            if (
              t &&
              (Math.abs(t.clientX - lpX) > 10 || Math.abs(t.clientY - lpY) > 10)
            )
              lpCancel();
          }

          function lpEnd(e: TouchEvent) {
            // when the press opened a menu, swallow the synthetic mouse
            // click that follows touchend so the menu stays open until the
            // user taps elsewhere
            if (lpFired) {
              e.preventDefault();
              lpFired = false;
            }
            lpCancel();
          }

          if (handler === "contextmenu") {
            element.addEventListener("touchstart", lpStart, { passive: true });
            element.addEventListener("touchmove", lpMove, { passive: true });
            element.addEventListener("touchend", lpEnd);
            element.addEventListener("touchcancel", lpCancel);
          }

          onCleanup(() => {
            element.removeEventListener(handler, onContextMenu);
            if (handler === "contextmenu") {
              element.removeEventListener("touchstart", lpStart);
              element.removeEventListener("touchmove", lpMove);
              element.removeEventListener("touchend", lpEnd);
              element.removeEventListener("touchcancel", lpCancel);
              lpCancel();
            }
          });
        }
      },
    ),
  );

  onCleanup(() => unregisterFloatingElement(element));
}
